import { Modal } from "@mui/material";
import { useContext, useEffect, useMemo, useState } from "react";
import { Material } from "../../../../data/entities";
import { ROLE } from "../../../../data/enums";
import { AuthenticationContext } from "../../../../provider/AuthenticationProvider";
import IconButton from "../../../../shared/components/IconButton";
import TextBox from "../../../../shared/components/TextBox";
import { StyledNavLink } from "../../../../shared/styled/TableStyles";
import { StyledButton } from "../../../../shared/styled/TitleBar";
import DeleteIcon from "../../../../shared/svg/DeleteIcon";
import { formatCurrency, formatInternationalNumber, parseInternationalToNumber } from "../../../../utils/format";
import { error } from "../../../../utils/toast";
import { StyledBackdrop, TitleText } from "../../../MaterialOrder/single/AddMaterialModal/Styled";
import { CountBadge, EmptyState } from "../../components";
import { useActiveMaterialsCatalog } from "../../hooks";
import {
    calculateProviderValue,
    materialHasProvider,
    normalizeMaterialForSelection,
} from "../../utils";
import {
    AddMaterialModalBody,
    AddMaterialModalFrame,
    MaterialIdentity,
    MaterialMetaLine,
    MaterialName,
    MaterialPickerLayout,
    MaterialPickerList,
    MaterialPickerPanel,
    MaterialPickerRowCard,
    MaterialTitleLine,
    MaterialUnavailableReason,
    ModalFooter,
    ModalHeaderRow,
    ModalStepItem,
    ModalStepNumber,
    ModalStepRail,
    ReviewField,
    ReviewList,
    ReviewMaterialCard,
    ReviewMaterialHeader,
    ReviewQuantityRow,
    ReviewRemoveAction,
    ReviewSourceTable,
    ReviewSourceTableWrapper,
    ReviewSourceTitle,
    ReviewSummaryBar,
    SelectedChip,
    SelectedChipList,
    SelectedSummaryPanel,
    SelectorPaneHeader,
    SelectorPaneTitle,
    SelectorSearchInput,
} from "../Styled";

type ModalStep = "select" | "review";

interface AddMaterialModalProps {
    selectedMaterials: Material[];
    providerId?: string | number | null;
    providerName?: string;
    open: boolean;
    handleClose: any;
    mergeOutsourceOrder: Function;
}

const getMaterialCode = (item: any) => item.material_code || item.id;

const hasOutsourceStandard = (item: any) => {
    const standards = item.outsource_standards;
    return Array.isArray(standards) && standards.length > 0;
};

const getMaterialSearchText = (material: any) =>
    [
        material.material_code,
        material.name,
        material.measurement_unit,
        material.storage,
    ].filter(Boolean).join(" ").toLowerCase();

const MaterialPickerRow = ({
    item,
    selected,
    toggleMaterial,
}: {
    item: any;
    selected: boolean;
    toggleMaterial: (material: Material) => void;
}) => (
    <MaterialPickerRowCard $selected={selected} $disabled={!hasOutsourceStandard(item)}>
        <MaterialIdentity>
            <MaterialTitleLine>
                <StyledNavLink to={`/materials/${item.id}`}>{getMaterialCode(item)}</StyledNavLink>
                <MaterialName>{item.name}</MaterialName>
            </MaterialTitleLine>
            <MaterialMetaLine>
                <span>Đơn vị: {item.measurement_unit || "-"}</span>
                <span>Tồn kho: {formatInternationalNumber(item.quantity ?? 0)}</span>
            </MaterialMetaLine>
            {!hasOutsourceStandard(item) && (
                <MaterialUnavailableReason>Chưa có định mức gia công, chưa chọn được</MaterialUnavailableReason>
            )}
        </MaterialIdentity>
        <StyledButton
            variant={selected ? "outlined" : "contained"}
            onClick={() => toggleMaterial(item)}
            disabled={!hasOutsourceStandard(item)}
        >
            {selected ? "Bỏ chọn" : "Chọn"}
        </StyledButton>
    </MaterialPickerRowCard>
);

export const AddMaterialModal = ({
    selectedMaterials,
    providerId,
    providerName,
    open,
    handleClose,
    mergeOutsourceOrder,
}: AddMaterialModalProps) => {
    const [isOpen, setIsOpen] = useState(open);
    const [step, setStep] = useState<ModalStep>("select");
    const { materials, isLoading, error: loadError } = useActiveMaterialsCatalog();
    const [selectedItems, setSelectedItems] = useState<any[]>(selectedMaterials);
    const [searchText, setSearchText] = useState("");
    const {
        user: { role },
    } = useContext(AuthenticationContext);

    const canSeePrice = ![ROLE.FIRST_STORAGE_KEEPER, ROLE.SECOND_STORAGE_KEEPER].includes(role);
    const pickerMaterials = useMemo(
        () => materials
            .filter((material: any) => materialHasProvider(material, providerId))
            .map((material: any) =>
                normalizeMaterialForSelection(material, providerId, providerName)
            ),
        [materials, providerId, providerName],
    );
    const filteredMaterials = useMemo(() => {
        const search = searchText.trim().toLowerCase();
        if (!search) return pickerMaterials;
        return pickerMaterials.filter((material: any) => getMaterialSearchText(material).includes(search));
    }, [pickerMaterials, searchText]);

    const normalizedSelectedItems = selectedItems.map((item: any) =>
        normalizeMaterialForSelection(item, providerId, providerName),
    );
    const totalOutsourceValue = calculateProviderValue(normalizedSelectedItems);
    const hasInvalidQuantity = normalizedSelectedItems.some((item: any) => (item.selectedQuantity || 0) <= 0);

    const removeSelectedItem = (removedItem: Material) => {
        const removedSelectedItems = selectedItems.filter(
            (selectedItem: Material) => selectedItem.id !== removedItem.id
        );
        setSelectedItems(removedSelectedItems);
        if (removedSelectedItems.length === 0) setStep("select");
    };

    const updateItemSelectedQuantity = (id: string, selectedQuantity: number) => {
        const updatedSelectedItems = selectedItems.map((material: any) => {
            if (material.id === id) {
                return { ...normalizeMaterialForSelection(material, providerId, providerName), selectedQuantity };
            }
            return material;
        });
        setSelectedItems(updatedSelectedItems);
    };

    const isMaterialSelected = (item: Material) => {
        return selectedItems.some((selectedItem: Material) => selectedItem.id === item.id);
    };

    const toggleMaterial = (item: Material) => {
        if (isMaterialSelected(item)) {
            removeSelectedItem(item);
            return;
        }
        if (!hasOutsourceStandard(item)) return;
        setSelectedItems([...selectedItems, normalizeMaterialForSelection(item, providerId, providerName)]);
    };

    const onClickAddMaterials = () => {
        const nextMaterials = selectedItems.map((item: any) =>
            normalizeMaterialForSelection(item, providerId, providerName),
        );
        mergeOutsourceOrder({ materials: nextMaterials });
        handleClose();
    };

    const handleCloseWithAnimation = () => {
        setIsOpen(false);
        setTimeout(handleClose, 200);
    };

    useEffect(() => {
        if (open) {
            setIsOpen(true);
            setSelectedItems(selectedMaterials.map((item: any) =>
                normalizeMaterialForSelection(item, providerId, providerName),
            ));
            setSearchText("");
            setStep("select");
        } else {
            const timer = setTimeout(() => setIsOpen(false), 200);
            return () => clearTimeout(timer);
        }
    }, [open, providerId, providerName, selectedMaterials]);

    return (
        <Modal
            open={open}
            onClose={handleCloseWithAnimation}
            closeAfterTransition
            BackdropComponent={StyledBackdrop}
            BackdropProps={{
                timeout: 500,
                onClick: (e) => e.stopPropagation(),
            }}
        >
            <AddMaterialModalFrame $isOpen={isOpen}>
                <ModalHeaderRow>
                    <TitleText>Thêm vật tư gia công</TitleText>
                    <ModalStepRail aria-label="Các bước thêm vật tư">
                        <ModalStepItem $active={step === "select"} $done={normalizedSelectedItems.length > 0}>
                            <ModalStepNumber $active={step === "select"} $done={normalizedSelectedItems.length > 0}>
                                1
                            </ModalStepNumber>
                            Chọn vật tư
                        </ModalStepItem>
                        <ModalStepItem $active={step === "review"} $done={step === "review" && !hasInvalidQuantity}>
                            <ModalStepNumber $active={step === "review"} $done={step === "review" && !hasInvalidQuantity}>
                                2
                            </ModalStepNumber>
                            Số lượng & đơn vị gia công
                        </ModalStepItem>
                    </ModalStepRail>
                </ModalHeaderRow>

                <AddMaterialModalBody>
                    {step === "select" && (
                        <MaterialPickerLayout data-testid="outsource-material-step-select">
                            <MaterialPickerPanel>
                                <SelectorPaneHeader>
                                    <SelectorPaneTitle>Kho vật tư</SelectorPaneTitle>
                                    <CountBadge>{filteredMaterials.length}</CountBadge>
                                </SelectorPaneHeader>
                                <SelectorSearchInput
                                    value={searchText}
                                    placeholder="Tìm mã vật tư, tên vật tư, đơn vị, kho..."
                                    onChange={(event) => setSearchText(event.target.value)}
                                />
                                <MaterialPickerList>
                                    {filteredMaterials.map((material: any) => (
                                        <MaterialPickerRow
                                            key={material.id}
                                            item={material}
                                            selected={isMaterialSelected(material)}
                                            toggleMaterial={toggleMaterial}
                                        />
                                    ))}
                                </MaterialPickerList>
                                {isLoading && (
                                    <EmptyState>
                                        <div className="title">Đang tải danh sách vật tư</div>
                                        <div className="body">Vui lòng chờ trong giây lát.</div>
                                    </EmptyState>
                                )}
                                {loadError && (
                                    <EmptyState>
                                        <div className="title">Không tải được danh sách vật tư</div>
                                        <div className="body">Vui lòng thử lại hoặc kiểm tra kết nối.</div>
                                    </EmptyState>
                                )}
                                {!isLoading && !loadError && filteredMaterials.length === 0 && (
                                    <EmptyState>
                                        <div className="title">Không tìm thấy vật tư</div>
                                        <div className="body">
                                            Thử đổi từ khóa tìm kiếm hoặc kiểm tra tab vật tư gia công của đơn vị gia công.
                                        </div>
                                    </EmptyState>
                                )}
                            </MaterialPickerPanel>

                            <SelectedSummaryPanel>
                                <SelectorPaneHeader>
                                    <SelectorPaneTitle>Đã chọn</SelectorPaneTitle>
                                    <CountBadge>{normalizedSelectedItems.length}</CountBadge>
                                </SelectorPaneHeader>
                                {normalizedSelectedItems.length > 0 ? (
                                    <SelectedChipList>
                                        {normalizedSelectedItems.map((item: any) => (
                                            <SelectedChip key={item.id}>
                                                {getMaterialCode(item)}
                                                <span>{item.name}</span>
                                            </SelectedChip>
                                        ))}
                                    </SelectedChipList>
                                ) : (
                                    <EmptyState>
                                        <div className="title">Chưa chọn vật tư</div>
                                        <div className="body">Chọn vật tư nguồn ở danh sách bên trái.</div>
                                    </EmptyState>
                                )}
                            </SelectedSummaryPanel>
                        </MaterialPickerLayout>
                    )}

                    {step === "review" && (
                        <ReviewList data-testid="outsource-material-step-review">
                            <SelectorPaneHeader>
                                <SelectorPaneTitle>
                                    Danh sách vật tư gia công của {providerName || normalizedSelectedItems[0]?.provider_name || "đơn vị gia công"}
                                </SelectorPaneTitle>
                                <CountBadge>{normalizedSelectedItems.length}</CountBadge>
                            </SelectorPaneHeader>
                            {normalizedSelectedItems.map((item: any) => (
                                <ReviewMaterialCard key={item.id}>
                                    <ReviewMaterialHeader>
                                        <MaterialIdentity>
                                            <MaterialTitleLine>
                                                <StyledNavLink to={`/materials/${item.id}`}>
                                                    {getMaterialCode(item)}
                                                </StyledNavLink>
                                                <MaterialName>{item.name}</MaterialName>
                                            </MaterialTitleLine>
                                            <MaterialMetaLine>
                                                <span>Đơn vị: {item.measurement_unit || "-"}</span>
                                                <span>Tồn kho: {formatInternationalNumber(item.quantity ?? 0)}</span>
                                                {canSeePrice && <span>Giá gia công: {formatCurrency(item.provider_price || 0)}</span>}
                                            </MaterialMetaLine>
                                        </MaterialIdentity>
                                        <ReviewRemoveAction>
                                            <IconButton
                                                aria-label={`Xoá ${item.name}`}
                                                onClick={() => removeSelectedItem(item)}
                                                style={{ width: 30, height: 30 }}
                                            >
                                                <DeleteIcon />
                                            </IconButton>
                                        </ReviewRemoveAction>
                                    </ReviewMaterialHeader>
                                    <ReviewQuantityRow>
                                        <ReviewField>
                                            Số lượng mang đi
                                            <TextBox
                                                numeric
                                                placeholder="Số lượng"
                                                text={formatInternationalNumber(item.selectedQuantity ?? "")}
                                                onChangeText={(value: string) => {
                                                    const n = parseInternationalToNumber(value, error);
                                                    if (!Number.isFinite(n)) return;
                                                    updateItemSelectedQuantity(item.id, n);
                                                }}
                                            />
                                        </ReviewField>
                                    </ReviewQuantityRow>
                                    <ReviewSourceTitle>Vật tư nguồn cần xuất thêm</ReviewSourceTitle>
                                    <ReviewSourceTableWrapper>
                                        <ReviewSourceTable>
                                            <thead>
                                                <tr>
                                                    <th>Mã vật tư</th>
                                                    <th>Vật tư nguồn</th>
                                                    <th>Định mức</th>
                                                    <th>Số lượng xuất</th>
                                                    <th>Đơn vị</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {(item.outsource_standards || []).map((standard: any) => {
                                                    const quantity = Number(item.selectedQuantity || 0);
                                                    const rate = Number(standard.source_quantity_per_output || 0);
                                                    const exportQuantity = Number((quantity * rate).toFixed(6));
                                                    return (
                                                        <tr key={standard.id}>
                                                            <td>
                                                                <StyledNavLink to={`/materials/${standard.id}`} target="_blank" rel="noopener noreferrer">
                                                                    {standard.material_code || standard.id}
                                                                </StyledNavLink>
                                                            </td>
                                                            <td>{standard.name}</td>
                                                            <td>{formatInternationalNumber(rate)}</td>
                                                            <td>{formatInternationalNumber(exportQuantity)}</td>
                                                            <td>{standard.measurement_unit || "-"}</td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </ReviewSourceTable>
                                    </ReviewSourceTableWrapper>
                                </ReviewMaterialCard>
                            ))}
                        </ReviewList>
                    )}
                </AddMaterialModalBody>

                {step === "review" && (
                    <ReviewSummaryBar>
                        <span>{normalizedSelectedItems.length} vật tư gia công</span>
                        {canSeePrice && <span>Tổng giá gia công: {formatCurrency(totalOutsourceValue)}</span>}
                    </ReviewSummaryBar>
                )}

                <ModalFooter>
                    <StyledButton variant="outlined" onClick={handleCloseWithAnimation}>Huỷ bỏ</StyledButton>
                    {step === "review" && (
                        <StyledButton variant="outlined" onClick={() => setStep("select")}>Quay lại</StyledButton>
                    )}
                    {step === "select" ? (
                        <StyledButton
                            variant="contained"
                            disabled={normalizedSelectedItems.length === 0}
                            onClick={() => setStep("review")}
                        >
                            Tiếp tục
                        </StyledButton>
                    ) : (
                        <StyledButton
                            variant="contained"
                            disabled={normalizedSelectedItems.length === 0 || hasInvalidQuantity}
                            onClick={onClickAddMaterials}
                        >
                            Lưu vật tư
                        </StyledButton>
                    )}
                </ModalFooter>
            </AddMaterialModalFrame>
        </Modal>
    );
};
